import { useState } from 'react';
import * as THREE from 'three';
import { formatScientificNotationSup } from './PlanetConstants';

const G = 6.674 * 10**-11; // Gravitational constant in m^3/kg/s^2
const M = 5.6834e26; // Mass of Saturn in kg

// Ring distances in meters (converted from kilometers)
const ringDistances = {
  D: 66000 * 10**3,   // D Ring ~66,000 km
  C: 74000 * 10**3,   // C Ring ~74,000 km
  B: 92000 * 10**3,   // B Ring ~92,000 km
  
  Cassini: 117000 * 10**3,  // Cassini Division ~117,000 km
  A: 136000 * 10**3,       // A Ring ~136,000 km
  
  F: 140000 * 10**3,   // F Ring ~140,000 km
  G: 170000 * 10**3,   // G Ring ~170,000 km
  E: 180000 * 10**3,   // E Ring ~180,000 km
};

// Function to calculate orbital velocity (v = sqrt(GM/r))
const orbitalVelocity = (r) => Math.sqrt(G * M / r);

// Function to calculate angular velocity (omega = v / r)
const angularVelocity = (v, r) => v / r;

// Refined function to calculate velocities for each ring and section
export const calculateRingVelocities = () => {
  const velocities = {};
  const angularVelocities = {};

  // Calculate orbital velocity and angular velocity for each individual ring
  for (const [ring, distance] of Object.entries(ringDistances)) {
    const velocity = orbitalVelocity(distance);
    const angularVel = angularVelocity(velocity, distance);
    
    velocities[ring] = velocity;
    angularVelocities[ring] = angularVel;
  }

  // Now you can decide to average the velocities or keep them individual
  const sectionVelocities = {
    inner: (angularVelocities.D + angularVelocities.C + angularVelocities.B) / 3,
    main: (angularVelocities.Cassini + angularVelocities.A) / 2,
    outer: (angularVelocities.F + angularVelocities.G + angularVelocities.E) / 3,
  };

  return sectionVelocities; // Return the angular velocities for each section
};

export function getRotationSpeed(name) {
  const periods = {
    Mercury: 1407.6,
    Venus: -5832.5,
    Earth: 24,
    Moon: 655.72,
    Mars: 24.6,
    Jupiter: 9.9,
    Saturn: 10.7,
    Uranus: -17.2,
    Neptune: 16.1,
    Pluto: -153.3,
    Sun: 609.12,

    // Kuiper Belt Objects
    Eris: 25.9,
    Haumea: 3.9155,
    Makemake: 22.83,
    Quaoar: 17.68,
    Sedna: 10.27,
    Orcus: 10.47,
    Ixion: 12.4,          // approximate
    "2002 TX300": 8.12,
    Salacia: 6.09,
    Gonggong: 22.4,
    "2002 MS4": 7.7,      // approximate
    Lempo: 12.5,          // approximate
    Varda: 5.91,
    Varuna: 6.34,
    Chaos: 9.0,           // approximate
    "2002 AW197": 8.8,    // approximate
    "2002 UX25": 14.38,
    "2002 TC302": 11.1,   // approximate
    "2003 AZ84": 6.71,
  };
  const hours = periods[name] || 24;
  return 1 / hours;
}

export function getAxialTilt(name) {
  const tilts = {
    Mercury: 0.03,
    Venus: 177.4,
    Earth: 23.5,
    Moon: 1.54,
    Mars: 25.2,
    Jupiter: 3.1,
    Saturn: 26.7,
    Uranus: 97.8,
    Neptune: 28.3,
    Pluto: 122.5,
    Sun: 7.25,

    // Kuiper Belt Objects
    Eris: 78.0,
    Haumea: 126.0,
    Makemake: 29.0,
    Quaoar: 8.0,
    Sedna: 12.0,          // estimated
    Orcus: 73.0,
    Ixion: 0.0,           // unknown
    "2002 TX300": 0.0,    // unknown
    Salacia: 0.0,         // unknown
    Gonggong: 83.0,
    "2002 MS4": 0.0,      // unknown
    Lempo: 0.0,           // unknown
    Varda: 0.0,           // unknown
    Varuna: 98.0,
    Chaos: 0.0,           // unknown
    "2002 AW197": 0.0,    // unknown
    "2002 UX25": 0.0,     // unknown
    "2002 TC302": 0.0,    // unknown
    "2003 AZ84": 0.0,     // unknown
    };
  return (tilts[name] || 0) * (Math.PI / 180);
}

export function convertRoundOrbitalSpeed(orbital_speed) 
{
    // convert to km/s
     return (orbital_speed / 1000).toFixed(2);
}

export function convertSiderealPeriod(siderealPeriod) {
  const earthYears = siderealPeriod / 365.25;
  return `${siderealPeriod} Earth days (~${earthYears.toFixed(2)} Earth years).`;
}

export function toJulianDate(date = new Date()) {
  const time = date.getTime();
  return (time / 86400000.0) + 2440587.5;
}

export function calculateOrbitalPhase(perihelionJD, siderealPeriod) {
  const currentJD = toJulianDate();
  const daysSincePerihelion = currentJD - perihelionJD;
  const rawPhase = (daysSincePerihelion % siderealPeriod) / siderealPeriod;
  const normalizedPhase = rawPhase < 0 ? rawPhase + 1 : rawPhase;
  const percentage = (normalizedPhase * 100).toFixed(1);
  return `${percentage}% of the orbit completed since last perihelion.`;
}

/**
 * Computes the current Earth's rotation angle in radians
 * based on elapsed time since J2000 and the Prime Meridian offset.
 * 
 * @param {number} primeMeridianOffset - In degrees, from J2000
 * @param {Date} [date=new Date()] - Optional date (defaults to now)
 * @returns {number} Rotation angle in radians
 */
export function getEarthRotationAngle(primeMeridianOffset, date = new Date()) {
  const j2000 = new Date('2000-01-01T12:00:00Z');
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const elapsedDays = (date - j2000) / MS_PER_DAY;
  const siderealRotationPerDay = 360.9856;

  const totalRotationDegrees = (primeMeridianOffset + elapsedDays * siderealRotationPerDay) % 360;

  return THREE.MathUtils.degToRad(totalRotationDegrees);
}

export function getApparentRadius(planetPos, cameraPos, baseRadius = 2, referenceDistance = 10) {
  const distance = new THREE.Vector3(...cameraPos).distanceTo(new THREE.Vector3(...planetPos));
  return (distance / referenceDistance) * baseRadius;
}


export function DistanceToggle({ AU }) {
  const [showKm, setShowKm] = useState(false);
  const AU_IN_KM = 149597870.7;

  const formatDistance = (auValue) => {
    // console.log("AU: " + auValue);
    if (typeof auValue !== 'number' || isNaN(auValue)) return 'N/A';

    // console.log("AU VALUE: " + auValue);
    return showKm
      ? `${(formatScientificNotationSup(auValue * AU_IN_KM)).toLocaleString()} km`
      : `${auValue.toFixed(3)} AU`;
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0.5rem 0',
      }}
    >
      <span>
        <strong>Distance from Sun:</strong> {formatDistance(AU)}
      </span>
      <div
        style={{
          display: 'flex',
          border: '1px solid #666',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <button
          onClick={() => setShowKm(false)}
          style={{
            padding: '4px 8px',
            background: !showKm ? '#fff' : '#444',
            color: !showKm ? '#000' : '#ccc',
            border: 'none',
            cursor: 'pointer',
            fontWeight: !showKm ? 'bold' : 'normal',
          }}
        >
          AU
        </button>
        <button
          onClick={() => setShowKm(true)}
          style={{
            padding: '4px 8px',
            background: showKm ? '#fff' : '#444',
            color: showKm ? '#000' : '#ccc',
            border: 'none',
            cursor: 'pointer',
            fontWeight: showKm ? 'bold' : 'normal',
          }}
        >
          km
        </button>
      </div>
    </div>
  );
}


